import React, { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { tasksApi } from "../../../api/tasks";
import { generateAIContent } from "../../../api/ai/generator";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { CaseTagSelector } from "../../../components/dashboard/cases/CaseTagSelector";
import {
  PlusIcon,
  CheckCircleIcon,
  XMarkIcon,
  TrashIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/24/solid";
import {
  Button,
  TextField,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import toast from "react-hot-toast";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import {
  mergeTodoWorkspaceParams,
  parseTodoFilter,
  parseTodoMine,
} from "./todoWorkspaceSearchParams";

const TODO_PAGE_SIZE = 30;

const RECURRENCE_OPTIONS = [
  { value: "none", labelKey: "todo.recurrenceNone" },
  { value: "daily", labelKey: "todo.recurrenceDaily" },
  { value: "weekdays", labelKey: "todo.recurrenceWeekdays" },
  { value: "weekly", labelKey: "todo.recurrenceWeekly" },
  { value: "monthly", labelKey: "todo.recurrenceMonthly" },
];

function isItemListLockedTask(task) {
  return !!(task?.description && task.description.includes("Item List ID:"));
}

function isHseDocumentModuleTask(task) {
  if (!task) return false;
  if (task.task_reference_type === "Hse::Document") return true;
  if (task.task_type === "hse_document_read") return true;
  if (task.description && task.description.includes("HSE_DOC_VERSION_ID:")) return true;
  return false;
}

export const TodoTaskList = ({ hideHeader = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = parseTodoFilter(searchParams);
  const showMine = parseTodoMine(searchParams);

  const patchTodoParams = useCallback(
    (patch) => {
      setSearchParams((prev) => mergeTodoWorkspaceParams(prev, patch), { replace: true });
    },
    [setSearchParams]
  );
  const [tasks, setTasks] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(hasMore);
  const pageRef = useRef(page);
  const fetchTasksPageRef = useRef(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [taskTagIds, setTaskTagIds] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const [hoursAvailable, setHoursAvailable] = useState("6");

  const fetchTasksPage = useCallback(
    async ({ page: pageNum, append }) => {
      const params = { page: pageNum, per_page: TODO_PAGE_SIZE };
      if (filter === "active") {
        params.finished = false;
      } else if (filter === "finished") {
        params.finished = true;
      }
      if (showMine) {
        params.mine = true;
      }

      if (append) {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await tasksApi.getTasks(params);
        if (response?.data?.success) {
          const rows = response.data.data || [];
          const meta = response.data.meta || {};
          const total = typeof meta.total === "number" ? meta.total : rows.length;

          setTotalCount(total);
          setError(null);

          let nextLength = 0;
          setTasks((prev) => {
            const next = append ? [...prev, ...rows] : rows;
            nextLength = next.length;
            return next;
          });
          const more = nextLength < total;
          setHasMore(more);
          hasMoreRef.current = more;
          setPage(pageNum);
          pageRef.current = pageNum;
        } else {
          setError("Kunne ikke hente oppgaver");
          if (!append) setTasks([]);
          hasMoreRef.current = false;
        }
      } catch (err) {
        console.error("Error fetching tasks:", err);
        if (append) {
          toast.error(i18n.t("todo.loadMoreError"));
        } else {
          setError("Kunne ikke hente oppgaver");
          setTasks([]);
          hasMoreRef.current = false;
        }
      } finally {
        loadingMoreRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filter, showMine, i18n]
  );

  loadingRef.current = loading;
  hasMoreRef.current = hasMore;
  pageRef.current = page;
  fetchTasksPageRef.current = fetchTasksPage;

  useEffect(() => {
    fetchTasksPage({ page: 1, append: false });
  }, [filter, showMine, fetchTasksPage]);

  useEffect(() => {
    if (loading && tasks.length === 0) return;

    const root = scrollContainerRef.current;
    const target = loadMoreSentinelRef.current;
    if (!root || !target) return;

    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) return;
        const nextPage = pageRef.current + 1;
        void fetchTasksPageRef.current?.({ page: nextPage, append: true });
      },
      { root, rootMargin: "120px", threshold: 0 }
    );
    io.observe(target);
    return () => io.disconnect();
  }, [loading, filter, showMine]);

  const handleCreateTask = async () => {
    if (!newTaskTitle.trim()) {
      toast.error("Tittel er påkrevd");
      return;
    }

    const title = newTaskTitle.trim();
    const tempId = `temp-${Date.now()}`;
    const newTask = {
      id: tempId,
      title,
      description: "",
      finished: false,
      active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Optimistic update
    setTasks((prev) => [newTask, ...prev]);
    setNewTaskTitle("");
    setIsCreating(false);

    try {
      const response = await tasksApi.createTask({
        title,
        description: "",
      });

      if (response?.data?.success) {
        // Replace temp task with real one
        setTasks((prev) =>
          prev.map((t) => (t.id === tempId ? response.data.data : t))
        );
        setTotalCount((c) => c + 1);
        toast.success("Oppgave opprettet");
      } else {
        // Rollback on error
        setTasks((prev) => prev.filter((t) => t.id !== tempId));
        toast.error("Kunne ikke opprette oppgave");
      }
    } catch (err) {
      console.error("Error creating task:", err);
      // Rollback on error
      setTasks((prev) => prev.filter((t) => t.id !== tempId));
      toast.error("Kunne ikke opprette oppgave");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleCreateTask();
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    // Optimistic update
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskId ? { ...t, ...updates, updated_at: new Date().toISOString() } : t
      )
    );

    // Update selected task if it's the one being updated
    if (selectedTask?.id === taskId) {
      setSelectedTask((prev) => ({ ...prev, ...updates }));
    }

    try {
      const response = await tasksApi.updateTask(taskId, updates);
      if (response?.data?.success) {
        // Update with server response
        setTasks((prev) =>
          prev.map((t) => (t.id === taskId ? response.data.data : t))
        );
        if (selectedTask?.id === taskId) {
          setSelectedTask(response.data.data);
        }
        toast.success("Oppgave oppdatert");
      } else {
        // Rollback on error
        fetchTasksPage({ page: 1, append: false });
        toast.error("Kunne ikke oppdatere oppgave");
      }
    } catch (err) {
      console.error("Error updating task:", err);
      // Rollback on error
      fetchTasksPage({ page: 1, append: false });
      toast.error("Kunne ikke oppdatere oppgave");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Er du sikker på at du vil slette denne oppgaven?")) {
      return;
    }

    // Optimistic update
    const taskToDelete = tasks.find((t) => t.id === taskId);
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    if (selectedTask?.id === taskId) {
      setSelectedTask(null);
    }

    try {
      const response = await tasksApi.deleteTask(taskId);
      if (response?.data?.success) {
        setTotalCount((c) => Math.max(0, c - 1));
        toast.success("Oppgave slettet");
      } else {
        // Rollback on error
        if (taskToDelete) {
          setTasks((prev) => [...prev, taskToDelete].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        }
        toast.error("Kunne ikke slette oppgave");
      }
    } catch (err) {
      console.error("Error deleting task:", err);
      // Rollback on error
      if (taskToDelete) {
        setTasks((prev) => [...prev, taskToDelete].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
      toast.error("Kunne ikke slette oppgave");
    }
  };

  const handleToggleFinished = async (taskId, currentStatus) => {
    const task = tasks.find((t) => t.id === taskId);

    // If task is linked to an absence, check if it's been processed
    if (task?.absence_id) {
      const absence = task.absence;
      if (!absence?.accepted && !absence?.denied) {
        toast.error("Kan ikke ferdigstille oppgave før fraværet er behandlet (godkjent eller avslått)");
        return;
      }
    }

    // If task is linked to an item handling list, prevent manual completion
    if (isItemListLockedTask(task)) {
      toast.error("Kan ikke ferdigstille oppgave manuelt. Den ferdigstilles automatisk når alle varer er håndtert.");
      return;
    }

    if (isHseDocumentModuleTask(task)) {
      toast.error(t("todo.hseDocumentTaskManualFinish"));
      return;
    }

    const newFinishedStatus = !currentStatus;

    // Optimistic update
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskId ? { ...t, finished: newFinishedStatus } : t
      )
    );

    if (selectedTask?.id === taskId) {
      setSelectedTask((prev) => ({ ...prev, finished: newFinishedStatus }));
    }

    try {
      const response = await tasksApi.toggleFinished(taskId);
      if (response?.data?.success) {
        // Update with server response
        setTasks((prev) =>
          prev.map((t) => (t.id === taskId ? response.data.data : t))
        );
        if (selectedTask?.id === taskId) {
          setSelectedTask(response.data.data);
        }
      } else {
        // Rollback on error
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId ? { ...t, finished: currentStatus } : t
          )
        );
        if (selectedTask?.id === taskId) {
          setSelectedTask((prev) => ({ ...prev, finished: currentStatus }));
        }
        toast.error("Kunne ikke oppdatere status");
      }
    } catch (err) {
      console.error("Error toggling finished status:", err);
      // Rollback on error
      setTasks((prev) =>
        prev.map((t) =>
          t.id === taskId ? { ...t, finished: currentStatus } : t
        )
      );
      if (selectedTask?.id === taskId) {
        setSelectedTask((prev) => ({ ...prev, finished: currentStatus }));
      }
      toast.error("Kunne ikke oppdatere status");
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setTaskTagIds((task.tag_definitions || []).map((x) => x.id));
  };

  const toDatetimeLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const fromDatetimeLocal = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  const runTodoAi = async (fields) => {
    if (!selectedTask) return;
    setAiLoading(true);
    setAiOpen(true);
    setAiText("");
    try {
      const openTasksSummary = tasks
        .filter((x) => !x.finished)
        .map((x) => x.title)
        .join("\n");
      const res = await generateAIContent({
        module: "todo",
        context: {
          task_title: selectedTask.title || "",
          task_description: selectedTask.description || "",
          open_tasks: openTasksSummary,
          hours_available: hoursAvailable || "6",
        },
        fields_to_generate: fields,
        provider: "grok",
        options: { language: "no", length: "short" },
      });
      if (res?.success && res.generated_fields) {
        const parts = Object.entries(res.generated_fields).map(([k, v]) => `**${k}**\n${v}`);
        setAiText(parts.join("\n\n"));
      } else {
        setAiText(res?.errors?.join?.("\n") || t("todo.aiError"));
      }
    } catch (e) {
      console.error(e);
      setAiText(t("todo.aiError"));
    } finally {
      setAiLoading(false);
    }
  };

  if (loading && tasks.length === 0) return <ResourceLoading />;
  if (error && tasks.length === 0) return <ResourceError error={error} />;

  return (
    <div className="h-full flex flex-col min-h-0">
      {!hideHeader && <DashboardHeader title={t("todo.pageTitle")} />}

      <div className="flex-1 overflow-hidden flex min-h-0">
        {/* Main Content */}
        <div ref={scrollContainerRef} className="flex-1 overflow-auto p-6">
          <div className="mx-auto max-w-4xl">
            {/* Filter Chips */}
            <div className="mb-6 flex flex-wrap gap-2">
              <Chip
                label={
                  filter === "all"
                    ? `${t("todo.filterAll")} (${totalCount})`
                    : t("todo.filterAll")
                }
                onClick={() => patchTodoParams({ status: "all" })}
                color={filter === "all" ? "primary" : "default"}
                clickable
              />
              <Chip
                label={
                  filter === "active"
                    ? `${t("todo.filterActive")} (${totalCount})`
                    : t("todo.filterActive")
                }
                onClick={() => patchTodoParams({ status: "active" })}
                color={filter === "active" ? "primary" : "default"}
                clickable
              />
              <Chip
                label={
                  filter === "finished"
                    ? `${t("todo.filterFinished")} (${totalCount})`
                    : t("todo.filterFinished")
                }
                onClick={() => patchTodoParams({ status: "finished" })}
                color={filter === "finished" ? "primary" : "default"}
                clickable
              />
              <Chip
                label={t("todo.filterMine")}
                onClick={() => patchTodoParams({ mine: !showMine })}
                color={showMine ? "secondary" : "default"}
                clickable
              />
            </div>

            {/* Tasks List */}
            <div className="space-y-3">
              {/* Create Task Input Row */}
              {isCreating ? (
                <div className="bg-white rounded-lg shadow-sm border-2 border-blue-300 p-3">
                  <div className="flex gap-2">
                    <TextField
                      fullWidth
                      placeholder="Skriv inn oppgavetittel..."
                      value={newTaskTitle}
                      onChange={(e) => setNewTaskTitle(e.target.value)}
                      onKeyPress={handleKeyPress}
                      autoFocus
                      variant="outlined"
                      size="small"
                    />
                    <Button
                      variant="contained"
                      onClick={handleCreateTask}
                      disabled={!newTaskTitle.trim()}
                      size="small"
                    >
                      Legg til
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setIsCreating(false);
                        setNewTaskTitle("");
                      }}
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </IconButton>
                  </div>
                </div>
              ) : (
                <div
                  className="bg-white rounded-lg shadow-sm border-2 border-dashed border-gray-300 p-3 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all"
                  onClick={() => setIsCreating(true)}
                >
                  <div className="flex items-center gap-2 text-gray-500">
                    <PlusIcon className="h-5 w-5" />
                    <span className="text-sm font-medium">Legg til ny oppgave</span>
                  </div>
                </div>
              )}

              {tasks.length === 0 && !isCreating ? (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg">Ingen oppgaver ennå</p>
                  <p className="text-sm mt-2">Klikk på &quot;Legg til ny oppgave&quot; for å legge til en oppgave</p>
                </div>
              ) : (
                tasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => handleTaskClick(task)}
                    className={`bg-white rounded-lg shadow-sm border-2 p-4 transition-all hover:shadow-md cursor-pointer ${
                      selectedTask?.id === task.id
                        ? "border-blue-500 ring-2 ring-blue-200"
                        : task.finished
                        ? "border-gray-200 opacity-75"
                        : "border-blue-200 hover:border-blue-300"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFinished(task.id, task.finished);
                        }}
                        className="mt-1 flex-shrink-0"
                        disabled={
                          (task.absence_id && !task.absence?.accepted && !task.absence?.denied) ||
                          isItemListLockedTask(task) ||
                          isHseDocumentModuleTask(task)
                        }
                        title={
                          task.absence_id && !task.absence?.accepted && !task.absence?.denied
                            ? "Kan ikke ferdigstilles før fraværet er behandlet"
                            : isItemListLockedTask(task)
                            ? "Kan ikke ferdigstilles manuelt. Ferdigstilles automatisk når alle varer er håndtert."
                            : isHseDocumentModuleTask(task)
                            ? t("todo.hseDocumentTaskManualFinish")
                            : ""
                        }
                      >
                        {task.finished ? (
                          <CheckCircleIconSolid className="h-6 w-6 text-green-500" />
                        ) : (
                          <CheckCircleIcon                           className={`h-6 w-6 ${
                            (task.absence_id && !task.absence?.accepted && !task.absence?.denied) ||
                            isItemListLockedTask(task) ||
                            isHseDocumentModuleTask(task)
                              ? "text-gray-300 cursor-not-allowed"
                              : "text-gray-400 hover:text-green-500"
                          }`} />
                        )}
                      </button>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3
                          className={`text-lg font-semibold ${
                            task.finished ? "line-through text-gray-500" : "text-gray-900"
                          }`}
                        >
                          {task.title}
                        </h3>
                        {task.description && (
                          <p
                            className={`mt-1 text-sm ${
                              task.finished ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {task.description}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                          {task.due_date && (
                            <Chip
                              size="small"
                              label={`${t("todo.due")}: ${task.due_date}`}
                              color={task.finished ? "default" : "warning"}
                              variant="outlined"
                            />
                          )}
                          {task.on_today_plan && (
                            <Chip size="small" label={t("todo.todayPlanBadge")} color="info" variant="outlined" />
                          )}
                          {(task.tag_definitions || []).map((tg) => (
                            <span
                              key={tg.id}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                              style={
                                tg.color
                                  ? { backgroundColor: `${tg.color}30`, color: tg.color }
                                  : { backgroundColor: "#f3f4f6", color: "#374151" }
                              }
                            >
                              {tg.name}
                            </span>
                          ))}
                          {task.customer && (
                            <Chip label={`Kunde: ${task.customer.name}`} size="small" variant="outlined" />
                          )}
                          {task.project && (
                            <Chip
                              label={`Prosjekt: ${task.project.name || task.project.project_number}`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {task.absence && (
                            <Chip
                              label={`Fravær: ${task.absence.displayTitle || task.absence.absence_number || `#${task.absence.id}`}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/absences/${task.absence.id}`);
                              }}
                              style={{ cursor: "pointer" }}
                            />
                          )}
                          {task?.description && task.description.includes("Item List ID:") && (() => {
                            const itemListIdMatch = task.description.match(/Item List ID: (\d+)/);
                            const itemListId = itemListIdMatch ? itemListIdMatch[1] : null;
                            return itemListId ? (
                              <Chip
                                label={`Varehåndtering: ${task.title.replace("Håndter vareliste - ", "")}`}
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/dashboard/inventory/item-handling/${itemListId}`);
                                }}
                                style={{ cursor: "pointer" }}
                              />
                            ) : null;
                          })()}
                          {isHseDocumentModuleTask(task) && task.task_reference_id && (
                            <Chip
                              label={t("todo.hseDocumentOpenLink")}
                              size="small"
                              variant="outlined"
                              color="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/hse-ks/documents/${task.task_reference_id}`);
                              }}
                              style={{ cursor: "pointer" }}
                            />
                          )}
                          <span>
                            Opprettet: {new Date(task.created_at).toLocaleDateString("no-NO")}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1 flex-shrink-0">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTask(task.id);
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </IconButton>
                      </div>
                    </div>
                  </div>
                ))
              )}
              {hasMore && !loading && tasks.length > 0 && (
                <div ref={loadMoreSentinelRef} className="h-12 w-full shrink-0" aria-hidden />
              )}
              {loadingMore && (
                <div className="py-3 text-center text-sm text-gray-500">{t("todo.loadingMoreTodos")}</div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        {selectedTask && (
          <div className="w-96 bg-white border-l border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t("todo.taskDetails")}</h2>
              <IconButton size="small" onClick={() => setSelectedTask(null)}>
                <XMarkIcon className="h-5 w-5" />
              </IconButton>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tittel
                </label>
                <TextField
                  fullWidth
                  value={selectedTask.title || ""}
                  onChange={(e) => {
                    const updated = { ...selectedTask, title: e.target.value };
                    setSelectedTask(updated);
                  }}
                  onBlur={() => {
                    if (selectedTask.title.trim()) {
                      handleUpdateTask(selectedTask.id, { title: selectedTask.title });
                    }
                  }}
                  variant="outlined"
                  size="small"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivelse
                </label>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={selectedTask.description || ""}
                  onChange={(e) => {
                    const updated = { ...selectedTask, description: e.target.value };
                    setSelectedTask(updated);
                  }}
                  onBlur={() => {
                    handleUpdateTask(selectedTask.id, { description: selectedTask.description || "" });
                  }}
                  placeholder={t("todo.descriptionPlaceholder")}
                  variant="outlined"
                  size="small"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("todo.dueDate")}</label>
                <TextField
                  fullWidth
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={selectedTask.due_date || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedTask((prev) => ({ ...prev, due_date: v || null }));
                  }}
                  onBlur={() =>
                    handleUpdateTask(selectedTask.id, { due_date: selectedTask.due_date || null })
                  }
                  variant="outlined"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("todo.reminder")}</label>
                <TextField
                  fullWidth
                  type="datetime-local"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={toDatetimeLocal(selectedTask.reminder_at)}
                  onChange={(e) => {
                    const iso = fromDatetimeLocal(e.target.value);
                    setSelectedTask((prev) => ({ ...prev, reminder_at: iso }));
                  }}
                  onBlur={() =>
                    handleUpdateTask(selectedTask.id, { reminder_at: selectedTask.reminder_at })
                  }
                  variant="outlined"
                />
              </div>

              <FormControlLabel
                control={
                  <Switch
                    checked={!!selectedTask.on_today_plan}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setSelectedTask((prev) => ({ ...prev, on_today_plan: v }));
                      handleUpdateTask(selectedTask.id, { on_today_plan: v });
                    }}
                  />
                }
                label={t("todo.onTodayPlan")}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("todo.recurrence")}</label>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedTask.recurrence || "none"}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedTask((prev) => ({ ...prev, recurrence: v }));
                    handleUpdateTask(selectedTask.id, { recurrence: v });
                  }}
                >
                  {RECURRENCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey)}
                    </MenuItem>
                  ))}
                </TextField>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("todo.estimatedHours")}</label>
                <TextField
                  fullWidth
                  type="number"
                  size="small"
                  inputProps={{ step: "0.25", min: 0 }}
                  value={selectedTask.estimated_hours ?? ""}
                  onChange={(e) => {
                    const v = e.target.value === "" ? null : e.target.value;
                    setSelectedTask((prev) => ({ ...prev, estimated_hours: v }));
                  }}
                  onBlur={() =>
                    handleUpdateTask(selectedTask.id, {
                      estimated_hours: selectedTask.estimated_hours,
                    })
                  }
                  variant="outlined"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("todo.labels")}</label>
                <CaseTagSelector
                  scopeModule="task"
                  value={taskTagIds}
                  onChange={(ids) => {
                    setTaskTagIds(ids);
                    handleUpdateTask(selectedTask.id, { tag_ids: ids });
                  }}
                  showBadges
                />
              </div>

              <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-slate-50">
                <div className="text-sm font-medium text-gray-800 flex items-center gap-1">
                  <SparklesIcon className="h-4 w-4" /> {t("todo.aiAssistant")}
                </div>
                <TextField
                  fullWidth
                  size="small"
                  label={t("todo.hoursAvailable")}
                  value={hoursAvailable}
                  onChange={(e) => setHoursAvailable(e.target.value)}
                />
                <div className="flex flex-wrap gap-1">
                  <Button size="small" variant="outlined" onClick={() => runTodoAi(["task_suggestions"])}>
                    {t("todo.aiSuggestions")}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => runTodoAi(["time_estimate"])}>
                    {t("todo.aiEstimate")}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => runTodoAi(["daily_plan"])}>
                    {t("todo.aiPlan")}
                  </Button>
                </div>
              </div>

              {/* Metadata */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <span className="text-sm font-medium">Informasjon</span>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Status:</span>{" "}
                      <span className={selectedTask.finished ? "text-green-600" : "text-blue-600"}>
                        {selectedTask.finished ? "Ferdig" : "Aktiv"}
                      </span>
                    </div>
                    {selectedTask.customer && (
                      <div>
                        <span className="font-medium text-gray-700">Kunde:</span>{" "}
                        {selectedTask.customer.name}
                      </div>
                    )}
                    {selectedTask.project && (
                      <div>
                        <span className="font-medium text-gray-700">Prosjekt:</span>{" "}
                        {selectedTask.project.name || selectedTask.project.project_number}
                      </div>
                    )}
                    {selectedTask.absence && (
                      <div>
                        <span className="font-medium text-gray-700">Fravær:</span>{" "}
                        <button
                          onClick={() => navigate(`/dashboard/absences/${selectedTask.absence.id}`)}
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {selectedTask.absence.displayTitle || selectedTask.absence.absence_number || `#${selectedTask.absence.id}`}
                        </button>
                        {selectedTask.absence.user && (
                          <span className="text-gray-600"> - {selectedTask.absence.user.displayTitle}</span>
                        )}
                        {selectedTask.absence.status && (
                          <span className={`ml-2 px-2 py-1 rounded text-xs ${
                            selectedTask.absence.accepted ? "bg-green-100 text-green-800" :
                            selectedTask.absence.denied ? "bg-red-100 text-red-800" :
                            "bg-yellow-100 text-yellow-800"
                          }`}>
                            {selectedTask.absence.accepted ? "Godkjent" :
                             selectedTask.absence.denied ? "Avslått" :
                             "Venter på behandling"}
                          </span>
                        )}
                      </div>
                    )}
                    {selectedTask?.description && selectedTask.description.includes("Item List ID:") && (() => {
                      const itemListIdMatch = selectedTask.description.match(/Item List ID: (\d+)/);
                      const itemListId = itemListIdMatch ? itemListIdMatch[1] : null;
                      return itemListId ? (
                        <div>
                          <span className="font-medium text-gray-700">Varehåndtering:</span>{" "}
                          <button
                            onClick={() => navigate(`/dashboard/inventory/item-handling/${itemListId}`)}
                            className="text-blue-600 hover:text-blue-800 underline"
                          >
                            {selectedTask.title.replace("Håndter vareliste - ", "")}
                          </button>
                          <span className={`ml-2 px-2 py-1 rounded text-xs ${
                            selectedTask.finished ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                          }`}>
                            {selectedTask.finished ? "Alle varer håndtert" : "Venter på håndtering"}
                          </span>
                        </div>
                      ) : null;
                    })()}
                    {isHseDocumentModuleTask(selectedTask) && selectedTask.task_reference_id && (
                      <div>
                        <span className="font-medium text-gray-700">HMS/KS:</span>{" "}
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/dashboard/hse-ks/documents/${selectedTask.task_reference_id}`)
                          }
                          className="text-blue-600 hover:text-blue-800 underline"
                        >
                          {t("todo.hseDocumentOpenLink")}
                        </button>
                        <span className="ml-2 text-xs text-gray-600">{t("todo.hseDocumentTaskManualFinish")}</span>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-gray-700">Opprettet:</span>{" "}
                      {new Date(selectedTask.created_at).toLocaleDateString("no-NO", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                    {selectedTask.updated_at && (
                      <div>
                        <span className="font-medium text-gray-700">Sist oppdatert:</span>{" "}
                        {new Date(selectedTask.updated_at).toLocaleDateString("no-NO", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>

              {/* Actions */}
              <div className="pt-4 border-t border-gray-200">
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    handleDeleteTask(selectedTask.id);
                  }}
                  startIcon={<TrashIcon className="h-5 w-5" />}
                >
                  Slett oppgave
                </Button>
              </div>
            </div>
          </div>
        )}

        <Dialog open={aiOpen} onClose={() => setAiOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{t("todo.aiResultTitle")}</DialogTitle>
          <DialogContent>
            {aiLoading ? (
              <div className="py-6 text-center text-gray-500">{t("todo.aiLoading")}</div>
            ) : (
              <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">{aiText}</pre>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAiOpen(false)}>{t("Close")}</Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};
